import React, { useEffect, useState, useRef } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError, API_BASE } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, FileText, Plus, Eye, Wand2, Trash2, Pencil, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const TIPO_DOC_BASE = {
  "33": "Factura Electrónica",
  "34": "Factura Exenta",
  "39": "Boleta Electrónica",
  "41": "Boleta Exenta",
  "56": "Nota de Débito",
  "61": "Nota de Crédito",
};

const TIPO_DOC_VENTA = {
  ...TIPO_DOC_BASE,
  "48": "Comprobante de Pago Electrónico",
};

const PAGE_SIZES = [10, 25, 50];

function tipoDocOptions(tipo) {
  return tipo === "venta" ? TIPO_DOC_VENTA : TIPO_DOC_BASE;
}

const EMPTY_FORM = {
  contacto_id: "", folio: "", tipo_doc: "33",
  fecha_emision: todayISO(), neto: "", iva: "", exento: "", total: "",
};

function facturaRevisarSii(f) {
  return Boolean(f?.sii_revisar) || (f?.observaciones && String(f.observaciones).includes("Revisar en SII"));
}

const MESES_RCV = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function formatPeriodoRcv(periodo) {
  if (!periodo || !/^\d{4}-\d{2}$/.test(periodo)) return periodo || "";
  const [y, m] = periodo.split("-");
  return `${MESES_RCV[parseInt(m, 10) - 1]} ${y}`;
}

function periodoToDateRange(periodo) {
  if (!periodo || !/^\d{4}-\d{2}$/.test(periodo)) return { desde: "", hasta: "" };
  const [y, m] = periodo.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return {
    desde: `${periodo}-01`,
    hasta: `${periodo}-${String(last).padStart(2, "0")}`,
  };
}

function LibroIVA({ tipo, titulo, endpointLibro, endpointFacturas, endpointImport, endpointContactos, testidPrefix }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState(todayISO());
  const [consolidados, setConsolidados] = useState([]);
  const [selectedConsolidado, setSelectedConsolidado] = useState("");
  const [deleteConsolidadoTarget, setDeleteConsolidadoTarget] = useState(null);
  const [contactos, setContactos] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteFacturaTarget, setDeleteFacturaTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [detalleFactura, setDetalleFactura] = useState(null);
  const [importReport, setImportReport] = useState(null);
  const [reemplazar, setReemplazar] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previews, setPreviews] = useState([]);    // [{factura_id, lineas, ...}]
  const [previewSelected, setPreviewSelected] = useState({}); // factura_id -> bool
  const [previewEstado, setPreviewEstado] = useState("borrador");
  const [previewSaving, setPreviewSaving] = useState(false);
  const [cuentas, setCuentas] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_FORM, fecha_emision: todayISO() });
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (selectedConsolidado) {
        params.import_batch_id = selectedConsolidado;
      } else {
        if (desde) params.desde = desde;
        if (hasta) params.hasta = hasta;
      }
      const { data } = await api.get(endpointLibro, { params });
      setData(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadConsolidados = async () => {
    try {
      const { data } = await api.get(`${endpointLibro}/consolidados`);
      setConsolidados(data || []);
    } catch {
      setConsolidados([]);
    }
  };

  const onSelectConsolidado = (batchId) => {
    setSelectedConsolidado(batchId);
    setPage(1);
    if (!batchId) {
      return;
    }
    const c = consolidados.find((x) => x.id === batchId);
    if (c?.periodo) {
      const { desde: d, hasta: h } = periodoToDateRange(c.periodo);
      setDesde(d);
      setHasta(h);
    }
  };

  const confirmDeleteConsolidado = async () => {
    if (!deleteConsolidadoTarget) return;
    try {
      const { data } = await api.delete(`${endpointLibro}/consolidados/${deleteConsolidadoTarget.id}`);
      toast.success(`Consolidado eliminado (${data.deleted} documento(s))`);
      if (selectedConsolidado === deleteConsolidadoTarget.id) {
        setSelectedConsolidado("");
      }
      setDeleteConsolidadoTarget(null);
      await loadConsolidados();
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const loadContactos = async () => {
    try {
      const { data } = await api.get(endpointContactos);
      setContactos(data);
    } catch {}
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [desde, hasta, selectedConsolidado, pageSize]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [desde, hasta, selectedConsolidado, page, pageSize]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadContactos(); loadConsolidados(); }, []);

  // Auto-calc total when neto + iva + exento changes
  const updateAmounts = (patch) => {
    const next = { ...form, ...patch };
    const neto = parseFloat(next.neto) || 0;
    const iva = parseFloat(next.iva) || (neto * 0.19);
    const exento = parseFloat(next.exento) || 0;
    if (!("iva" in patch) && next.neto) next.iva = Math.round(neto * 0.19);
    next.total = Math.round(neto + (parseFloat(next.iva) || iva) + exento);
    setForm(next);
  };

  const resetFormDialog = () => {
    setOpenNew(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM, fecha_emision: todayISO() });
  };

  const openEditFactura = (f) => {
    setEditingId(f.id);
    setForm({
      contacto_id: f.contacto_id || "",
      folio: String(f.folio ?? ""),
      tipo_doc: f.tipo_doc || "33",
      fecha_emision: f.fecha_emision || todayISO(),
      neto: f.neto ?? "",
      iva: f.iva ?? "",
      exento: f.exento ?? "",
      total: f.total ?? "",
    });
    setOpenNew(true);
  };

  const submitFactura = async (e) => {
    e.preventDefault();
    const payload = {
      contacto_id: form.contacto_id,
      folio: form.folio,
      tipo_doc: form.tipo_doc,
      fecha_emision: form.fecha_emision,
      neto: parseFloat(form.neto) || 0,
      iva: parseFloat(form.iva) || 0,
      exento: parseFloat(form.exento) || 0,
      total: parseFloat(form.total) || 0,
    };
    try {
      if (editingId) {
        await api.put(`${endpointFacturas}/${editingId}`, payload);
        toast.success("Factura actualizada");
      } else {
        await api.post(endpointFacturas, payload);
        toast.success("Factura registrada");
      }
      resetFormDialog();
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const confirmDeleteFactura = async () => {
    if (!deleteFacturaTarget) return;
    try {
      await api.delete(`${endpointFacturas}/${deleteFacturaTarget.id}`);
      toast.success("Documento eliminado");
      setDeleteFacturaTarget(null);
      if (data?.facturas?.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        load();
      }
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      toast.info(reemplazar ? "Reimportando archivo SII (reemplazar duplicadas)…" : "Importando archivo SII…");
      const { data } = await api.post(`${endpointImport}?reemplazar=${reemplazar}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportReport(data);
      const msg = [
        `Nuevas: ${data.imported}`,
        data.replaced ? `Reemplazadas: ${data.replaced}` : null,
        `Omitidas: ${data.skipped}`,
        `Nuevos ${tipo === "venta" ? "clientes" : "proveedores"}: ${data.new_contactos}`,
      ].filter(Boolean).join(" · ");
      toast.success(msg);
      if (data.warnings?.length) {
        toast.warning(`${data.warnings_count} factura(s) con descuadre — ver detalle abajo`);
      }
      if (data.errors?.length) {
        toast.error(`Con ${data.errors.length} errores — ver detalle abajo`);
      }
      if (data.batch_id) {
        await loadConsolidados();
        setSelectedConsolidado(data.batch_id);
        if (data.periodo_rcv) {
          const { desde: d, hasta: h } = periodoToDateRange(data.periodo_rcv);
          setDesde(d);
          setHasta(h);
        }
      }
      load();
      loadContactos();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const exportQueryParams = () => {
    const params = new URLSearchParams();
    if (selectedConsolidado) {
      params.set("import_batch_id", selectedConsolidado);
    } else {
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
    }
    return params;
  };

  const downloadExcel = () => {
    window.open(`${API_BASE}${endpointLibro}/excel?${exportQueryParams()}`, "_blank");
  };

  const downloadPdf = () => {
    window.open(`${API_BASE}${endpointLibro}/pdf?${exportQueryParams()}`, "_blank");
  };

  const cuentasImputables = cuentas
    .filter((c) => c.imputable)
    .sort((a, b) => a.codigo.localeCompare(b.codigo));

  const openPreviewAsientos = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const [previewRes, cuentasRes] = await Promise.all([
        api.get(`/asientos/preview-desde-facturas/${tipo}`, { params }),
        cuentas.length ? Promise.resolve({ data: cuentas }) : api.get("/cuentas"),
      ]);
      if (!cuentas.length) setCuentas(cuentasRes.data || []);
      const { data } = previewRes;
      setPreviews(data.previews || []);
      const sel = {};
      (data.previews || []).forEach((p) => { sel[p.factura_id] = true; });
      setPreviewSelected(sel);
      if ((data.previews || []).length === 0) {
        toast.info("No hay facturas pendientes de asiento en el período seleccionado.");
      }
      if ((data.errores || []).length) {
        toast.warning(`${data.errores.length} factura(s) no se pudieron previsualizar.`);
      }
    } catch (err) {
      toast.error(extractError(err));
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const updatePreviewLinea = (facturaIdx, lineaIdx, field, value) => {
    setPreviews((prev) => {
      const next = [...prev];
      const p = { ...next[facturaIdx] };
      const lineas = p.lineas.map((l, i) => {
        if (i !== lineaIdx) return l;
        if (field === "cuenta_id") {
          const c = cuentasImputables.find((x) => x.id === value);
          return c
            ? { ...l, cuenta_id: c.id, cuenta_codigo: c.codigo, cuenta_nombre: c.nombre, cuenta_tipo: c.tipo }
            : { ...l, cuenta_id: value };
        }
        if (field === "glosa") return { ...l, glosa: value };
        return { ...l, [field]: parseFloat(value) || 0 };
      });
      p.lineas = lineas;
      p.total_debito = lineas.reduce((s, l) => s + (l.debito || 0), 0);
      p.total_credito = lineas.reduce((s, l) => s + (l.credito || 0), 0);
      next[facturaIdx] = p;
      return next;
    });
  };

  const updatePreviewGlosa = (facturaIdx, value) => {
    setPreviews((prev) => {
      const next = [...prev];
      next[facturaIdx] = { ...next[facturaIdx], glosa_propuesta: value };
      return next;
    });
  };

  const confirmAsientos = async () => {
    const toSend = previews
      .filter((p) => previewSelected[p.factura_id])
      .map((p) => ({
        factura_id: p.factura_id,
        fecha: p.factura_fecha,
        glosa: p.glosa_propuesta,
        lineas: p.lineas,
      }));
    if (toSend.length === 0) {
      toast.warning("No has seleccionado ninguna factura");
      return;
    }
    setPreviewSaving(true);
    try {
      const { data } = await api.post(`/asientos/confirmar-desde-facturas`, {
        tipo,
        asientos: toSend,
        estado: previewEstado,
      });
      toast.success(`${data.creados} asiento(s) ${previewEstado === "registrado" ? "registrados" : "creados en borrador"}`);
      if ((data.errores || []).length) {
        toast.warning(`${data.errores.length} con errores: ${data.errores.slice(0, 3).join("; ")}`);
      }
      setPreviewOpen(false);
      setPreviews([]);
      setPreviewSelected({});
      load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setPreviewSaving(false);
    }
  };

  return (
    <Page>
      <PageHeader
        title={titulo}
        subtitle={`Registro de ${tipo === "venta" ? "ventas" : "compras"} (IVA)`}
        testid={`${testidPrefix}-header`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid={`${testidPrefix}-desde`}
              className="px-2 py-1.5 border border-border rounded-sm text-sm" />
            <span className="text-xs text-muted-foreground">→</span>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid={`${testidPrefix}-hasta`}
              className="px-2 py-1.5 border border-border rounded-sm text-sm" />

            <input type="file" ref={fileRef} onChange={handleImport} accept=".xlsx,.xls,.csv"
              data-testid={`${testidPrefix}-file-input`} className="hidden" />
            <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none px-2 py-1.5 rounded-sm hover:bg-muted" title="Si marcas esto, las facturas que ya existen serán reemplazadas con los datos del nuevo archivo SII">
              <input type="checkbox" checked={reemplazar} onChange={(e) => setReemplazar(e.target.checked)}
                data-testid={`${testidPrefix}-reemplazar`} />
              Reemplazar duplicadas
            </label>
            <button onClick={() => fileRef.current?.click()} data-testid={`${testidPrefix}-import`}
              className="inline-flex items-center gap-1.5 border border-warning text-warning-foreground hover:bg-warning/10 text-sm px-3 py-1.5 rounded-sm">
              <Upload className="h-4 w-4" /> Importar SII
            </button>

            <button onClick={downloadExcel} data-testid={`${testidPrefix}-excel`}
              className="inline-flex items-center gap-1.5 border border-success text-success hover:bg-success/10 text-sm px-3 py-1.5 rounded-sm">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </button>

            <button onClick={downloadPdf} data-testid={`${testidPrefix}-pdf`}
              className="inline-flex items-center gap-1.5 border border-success text-success hover:bg-success/10 text-sm px-3 py-1.5 rounded-sm"
              title="Descargar o imprimir libro en PDF">
              <FileText className="h-4 w-4" /> PDF
            </button>

            <button onClick={openPreviewAsientos} data-testid={`${testidPrefix}-generar-asientos`}
              className="inline-flex items-center gap-1.5 border border-warning-foreground text-warning-foreground hover:bg-warning/15 text-sm px-3 py-1.5 rounded-sm"
              title="Genera asientos contables automáticos desde las facturas sin asiento (con preview editable)">
              <Wand2 className="h-4 w-4" /> Generar asientos
            </button>

            <Dialog open={openNew} onOpenChange={(v) => { if (!v) resetFormDialog(); else setOpenNew(true); }}>
              <DialogTrigger asChild>
                <button data-testid={`new-${testidPrefix}`}
                  onClick={() => { setEditingId(null); setForm({ ...EMPTY_FORM, fecha_emision: todayISO() }); }}
                  className="inline-flex items-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-1.5 rounded-sm">
                  <Plus className="h-4 w-4" /> Nueva factura
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingId ? "Editar factura" : `Nueva factura de ${tipo}`}</DialogTitle>
                  <DialogDescription>
                    {editingId
                      ? "Modifica un documento ingresado manualmente."
                      : "Registra una factura manualmente (también puedes importar desde el SII)."}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submitFactura} className="space-y-3" data-testid={`form-${testidPrefix}`}>
                  <div>
                    <label className="label-caps block mb-1">{tipo === "venta" ? "Cliente" : "Proveedor"}</label>
                    <Select value={form.contacto_id} onValueChange={(v) => setForm({ ...form, contacto_id: v })}>
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder={`Seleccionar ${tipo === "venta" ? "cliente" : "proveedor"}...`} />
                      </SelectTrigger>
                      <SelectContent>
                        {contactos.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <span className="font-mono mr-2">{c.rut}</span>{c.razon_social}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {contactos.length === 0 && (
                      <p className="text-xs text-destructive mt-1">No hay {tipo === "venta" ? "clientes" : "proveedores"}. Crea uno primero.</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="label-caps block mb-1">Tipo Doc</label>
                      <Select value={form.tipo_doc} onValueChange={(v) => setForm({ ...form, tipo_doc: v })}>
                        <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(tipoDocOptions(tipo)).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{k} · {v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="label-caps block mb-1">Folio</label>
                      <input required value={form.folio} onChange={(e) => setForm({ ...form, folio: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm font-mono" />
                    </div>
                    <div>
                      <label className="label-caps block mb-1">Fecha</label>
                      <input required type="date" value={form.fecha_emision} onChange={(e) => setForm({ ...form, fecha_emision: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="label-caps block mb-1">Neto</label>
                      <input type="number" value={form.neto} onChange={(e) => updateAmounts({ neto: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm text-right font-mono" />
                    </div>
                    <div>
                      <label className="label-caps block mb-1">Exento</label>
                      <input type="number" value={form.exento} onChange={(e) => updateAmounts({ exento: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm text-right font-mono" />
                    </div>
                    <div>
                      <label className="label-caps block mb-1">IVA</label>
                      <input type="number" value={form.iva} onChange={(e) => updateAmounts({ iva: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm text-right font-mono" />
                    </div>
                    <div>
                      <label className="label-caps block mb-1">Total</label>
                      <input type="number" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-sm text-sm text-right font-mono font-bold" />
                    </div>
                  </div>
                  <DialogFooter>
                    <button type="submit" disabled={!form.contacto_id}
                      className="bg-primary hover:bg-success disabled:opacity-50 text-primary-foreground text-sm font-medium px-4 py-2 rounded-sm">
                      {editingId ? "Guardar cambios" : "Registrar"}
                    </button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3 p-3 bg-card border border-border rounded-md" data-testid={`${testidPrefix}-consolidados`}>
        <div className="min-w-[220px] flex-1">
          <label className="label-caps block mb-1">Consolidado RCV (mes SII)</label>
          <Select
            value={selectedConsolidado || "__all__"}
            onValueChange={(v) => onSelectConsolidado(v === "__all__" ? "" : v)}
          >
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Todos los documentos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos (filtro por fechas)</SelectItem>
              {consolidados.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {formatPeriodoRcv(c.periodo)} · {c.count} doc. · {c.filename?.length > 40 ? `${c.filename.slice(0, 37)}…` : c.filename}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selectedConsolidado && (
          <>
            <p className="text-xs text-muted-foreground pb-2">
              Mostrando el consolidado importado del SII
              {consolidados.find((c) => c.id === selectedConsolidado)?.periodo && (
                <> ({formatPeriodoRcv(consolidados.find((c) => c.id === selectedConsolidado).periodo)})</>
              )}
            </p>
            <button
              type="button"
              onClick={() => setDeleteConsolidadoTarget(consolidados.find((c) => c.id === selectedConsolidado))}
              className="inline-flex items-center gap-1 text-sm text-destructive border border-destructive/30 px-3 py-2 rounded-sm hover:bg-destructive/10"
              data-testid={`${testidPrefix}-delete-consolidado`}
            >
              <Trash2 className="h-4 w-4" /> Eliminar consolidado
            </button>
          </>
        )}
      </div>

      <AlertDialog open={!!deleteFacturaTarget} onOpenChange={(v) => !v && setDeleteFacturaTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar documento manual?</AlertDialogTitle>
            <AlertDialogDescription>
              Se borrará el documento <strong>#{deleteFacturaTarget?.folio}</strong> de{" "}
              <strong>{deleteFacturaTarget?.contacto_nombre}</strong> ({deleteFacturaTarget?.contacto_rut}).
              No se puede deshacer. Si tiene cobros/pagos imputados o un asiento autorizado, la operación será rechazada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteFactura} className="bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteConsolidadoTarget} onOpenChange={(v) => !v && setDeleteConsolidadoTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar consolidado importado?</AlertDialogTitle>
            <AlertDialogDescription>
              Se borrarán <strong>{deleteConsolidadoTarget?.count ?? 0}</strong> documento(s) de{" "}
              <strong>{formatPeriodoRcv(deleteConsolidadoTarget?.periodo)}</strong>
              {deleteConsolidadoTarget?.filename ? ` (${deleteConsolidadoTarget.filename})` : ""}.
              No se puede deshacer. Si alguno tiene asiento contable, la operación será rechazada.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteConsolidado} className="bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {importReport && (importReport.warnings?.length > 0 || importReport.errors?.length > 0) && (
        <div className="mb-4 bg-warning-surface border border-warning rounded-md p-4" data-testid={`${testidPrefix}-import-report`}>
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="label-caps text-warning-foreground">Reporte de importación</div>
              <div className="text-sm text-foreground mt-1">
                Nuevas: <strong>{importReport.imported}</strong>
                {importReport.replaced > 0 && <> {" · "}Reemplazadas: <strong>{importReport.replaced}</strong></>}
                {" · "}Omitidas (duplicadas): <strong>{importReport.skipped}</strong>
                {" · "}Nuevos {tipo === "venta" ? "clientes" : "proveedores"}: <strong>{importReport.new_contactos}</strong>
              </div>
            </div>
            <button onClick={() => setImportReport(null)} className="text-muted-foreground hover:text-foreground text-sm">✕</button>
          </div>

          {importReport.warnings?.length > 0 && (
            <div className="mt-3">
              <div className="label-caps text-destructive mb-2">
                ⚠ {importReport.warnings_count} factura(s) con descuadre matemático
              </div>
              <div className="bg-card border border-border rounded-sm overflow-x-auto">
                <table className="acc-table">
                  <thead>
                    <tr>
                      <th>Folio</th>
                      <th>RUT</th>
                      <th>Razón social</th>
                      <th className="num">Total documento</th>
                      <th className="num">Suma componentes</th>
                      <th className="num">Diferencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importReport.warnings.map((w, i) => (
                      <tr key={i}>
                        <td className="font-mono">#{w.folio}</td>
                        <td className="font-mono text-[12px]">{w.rut}</td>
                        <td>{w.razon_social}</td>
                        <td className="num">{fmtCLP(w.total)}</td>
                        <td className="num">{fmtCLP(w.suma_componentes)}</td>
                        <td className="num font-semibold text-destructive">{fmtCLP(w.diferencia)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Las facturas se importan con el <strong>Monto Total</strong> del SII. Si queda descuadre, suele ser combustibles u otros
                impuestos donde el total pagado no coincide con neto+IVA+recargos; el ícono <span className="font-mono">👁</span> muestra el detalle
                (incluye filas adicionales de tabacos/otros impuestos del CSV).
              </p>
            </div>
          )}

          {importReport.errors?.length > 0 && (
            <div className="mt-3">
              <div className="label-caps text-destructive mb-2">✗ Errores</div>
              <ul className="text-xs text-destructive space-y-1 font-mono">
                {importReport.errors.map((e, i) => <li key={i}>· {e}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
          <KPI label="Documentos" value={data.totales.count.toLocaleString("es-CL")} />
          <KPI label="Exento" value={fmtCLP(data.totales.exento)} />
          <KPI label="Neto" value={fmtCLP(data.totales.neto)} />
          <KPI label="IVA Recup." value={fmtCLP(data.totales.iva)} tone="warning" />
          {data.totales.iva_no_recuperable > 0 && (
            <KPI label="IVA No Recup." value={fmtCLP(data.totales.iva_no_recuperable)} tone="danger" />
          )}
          <KPI label="Total" value={fmtCLP(data.totales.total)} tone="primary" />
        </div>
      )}

      {data && (data.totales.iva_uso_comun > 0 || data.totales.iva_activo_fijo > 0 || data.totales.impto_sin_derecho_credito > 0) && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {data.totales.iva_uso_comun > 0 && <KPI label="IVA Uso Común" value={fmtCLP(data.totales.iva_uso_comun)} />}
          {data.totales.monto_neto_activo_fijo > 0 && <KPI label="Neto Activo Fijo" value={fmtCLP(data.totales.monto_neto_activo_fijo)} />}
          {data.totales.iva_activo_fijo > 0 && <KPI label="IVA Activo Fijo" value={fmtCLP(data.totales.iva_activo_fijo)} />}
          {data.totales.impto_sin_derecho_credito > 0 && <KPI label="Impto. sin Crédito" value={fmtCLP(data.totales.impto_sin_derecho_credito)} />}
        </div>
      )}

      <div className="bg-card border border-border rounded-md overflow-auto">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
        ) : !data || data.facturas.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            Sin facturas en el período. Puedes importar el CSV/XLSX del SII o crear manualmente.
          </div>
        ) : (
          <table className="acc-table" data-testid={`${testidPrefix}-table`}>
            <thead>
              <tr>
                <th style={{ width: 90 }}>Fecha</th>
                <th style={{ width: 90 }}>Tipo Doc</th>
                {tipo === "compra" && <th style={{ width: 90 }}>Tipo Compra</th>}
                <th style={{ width: 80 }}>Folio</th>
                <th style={{ width: 120 }}>RUT</th>
                <th>{tipo === "venta" ? "Cliente" : "Proveedor"}</th>
                <th className="num">Neto</th>
                <th className="num">IVA</th>
                {tipo === "compra" && <th className="num">IVA No Rec.</th>}
                <th className="num">Total</th>
                <th style={{ width: 88 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.facturas.map((f) => (
                <tr key={f.id}>
                  <td>{fmtDate(f.fecha_emision)}</td>
                  <td className="text-[11px]">{f.tipo_doc_nombre || f.tipo_doc}</td>
                  {tipo === "compra" && <td className="text-[11px] text-muted-foreground">{f.tipo_compra || "—"}</td>}
                  <td className="font-mono text-[12px]">
                    #{f.folio}
                    {facturaRevisarSii(f) && (
                      <span
                        className="ml-1 inline-block text-[9px] font-sans font-semibold uppercase tracking-wide text-warning-foreground bg-warning-surface border border-warning px-1 py-0.5 rounded-sm align-middle"
                        title={f.observaciones || "Revisar en SII"}
                      >
                        Revisar SII
                      </span>
                    )}
                  </td>
                  <td className="font-mono text-[12px]">{f.contacto_rut}</td>
                  <td>{f.contacto_nombre}</td>
                  <td className="num">{f.neto ? fmtCLP(f.neto) : ""}</td>
                  <td className="num">{f.iva ? fmtCLP(f.iva) : ""}</td>
                  {tipo === "compra" && <td className="num text-destructive">{f.iva_no_recuperable ? fmtCLP(f.iva_no_recuperable) : ""}</td>}
                  <td className="num font-semibold">{fmtCLP(f.total)}</td>
                  <td>
                    <div className="flex items-center gap-0.5">
                      {f.fuente === "manual" && (
                        <>
                          <button
                            onClick={() => openEditFactura(f)}
                            title="Editar documento manual"
                            data-testid={`edit-${f.id}`}
                            className="p-1 rounded-sm text-muted-foreground hover:text-primary hover:bg-muted"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => setDeleteFacturaTarget(f)}
                            title="Eliminar documento manual"
                            data-testid={`delete-${f.id}`}
                            className="p-1 rounded-sm text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setDetalleFactura(f)}
                        title={facturaRevisarSii(f) ? "Ver detalle SII — Revisar en SII" : "Ver detalle"}
                        data-testid={`detalle-${f.id}`}
                        className={`p-1 rounded-sm ${
                          facturaRevisarSii(f)
                            ? "text-warning-foreground hover:bg-warning-surface"
                            : "text-muted-foreground hover:text-primary hover:bg-muted"
                        }`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-muted font-semibold">
                <td colSpan={tipo === "compra" ? 6 : 5} className="text-right pr-3 label-caps">Totales</td>
                <td className="num">{fmtCLP(data.totales.neto)}</td>
                <td className="num text-warning-foreground">{fmtCLP(data.totales.iva)}</td>
                {tipo === "compra" && <td className="num text-destructive">{fmtCLP(data.totales.iva_no_recuperable)}</td>}
                <td className="num text-primary">{fmtCLP(data.totales.total)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}
        {data?.pagination && data.pagination.total > 0 && (
          <div
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border bg-background"
            data-testid={`${testidPrefix}-pagination`}
          >
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Filas por página</span>
              <Select
                value={String(pageSize)}
                onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}
              >
                <SelectTrigger className="h-8 w-[72px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((n) => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="text-xs text-muted-foreground">
              {((data.pagination.page - 1) * data.pagination.page_size) + 1}
              –
              {Math.min(data.pagination.page * data.pagination.page_size, data.pagination.total)}
              {" "}de{" "}
              {data.pagination.total.toLocaleString("es-CL")}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={data.pagination.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid={`${testidPrefix}-page-prev`}
                className="inline-flex items-center gap-1 border border-border disabled:opacity-40 hover:bg-card text-xs px-2.5 py-1.5 rounded-sm"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Anterior
              </button>
              <span className="text-xs text-muted-foreground px-2">
                {data.pagination.page} / {data.pagination.total_pages}
              </span>
              <button
                type="button"
                disabled={data.pagination.page >= data.pagination.total_pages}
                onClick={() => setPage((p) => p + 1)}
                data-testid={`${testidPrefix}-page-next`}
                className="inline-flex items-center gap-1 border border-border disabled:opacity-40 hover:bg-card text-xs px-2.5 py-1.5 rounded-sm"
              >
                Siguiente <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!detalleFactura} onOpenChange={(v) => !v && setDetalleFactura(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle factura · Folio #{detalleFactura?.folio}</DialogTitle>
            <DialogDescription>Detalle de montos y datos originales del SII.</DialogDescription>
          </DialogHeader>
          {detalleFactura && (
            <div className="space-y-4">
              {facturaRevisarSii(detalleFactura) && (
                <div className="bg-warning-surface border border-warning rounded-md px-3 py-2 text-sm text-warning-foreground">
                  <strong>Revisar en SII</strong>
                  {detalleFactura.observaciones && (
                    <p className="mt-1 text-muted-foreground">{detalleFactura.observaciones}</p>
                  )}
                  {detalleFactura.sii_descuadre && (
                    <p className="mt-1 text-xs font-mono text-muted-foreground">
                      Total documento {fmtCLP(detalleFactura.sii_descuadre.total_documento)} · componentes{" "}
                      {fmtCLP(detalleFactura.sii_descuadre.suma_componentes)} · dif.{" "}
                      {fmtCLP(detalleFactura.sii_descuadre.diferencia)}
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <Info label="Tipo Doc" value={detalleFactura.tipo_doc_nombre} />
                <Info label="Tipo Compra" value={detalleFactura.tipo_compra || "—"} />
                <Info label="Fecha emisión" value={fmtDate(detalleFactura.fecha_emision)} />
                <Info label="Fecha recepción" value={detalleFactura.fecha_recepcion ? fmtDate(detalleFactura.fecha_recepcion) : "—"} />
                <Info label="RUT" value={detalleFactura.contacto_rut} mono />
                <Info label="Razón social" value={detalleFactura.contacto_nombre} />
              </div>
              <div className="border-t border-border pt-3">
                <div className="label-caps mb-2">Detalle de montos</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {[
                    ["Exento", detalleFactura.exento],
                    ["Neto", detalleFactura.neto],
                    ["IVA Recuperable", detalleFactura.iva],
                    ["IVA No Recuperable", detalleFactura.iva_no_recuperable],
                    ["IVA Uso Común", detalleFactura.iva_uso_comun],
                    ["Neto Activo Fijo", detalleFactura.monto_neto_activo_fijo],
                    ["IVA Activo Fijo", detalleFactura.iva_activo_fijo],
                    ["Impto. sin Derecho a Crédito", detalleFactura.impto_sin_derecho_credito],
                    ["IVA No Retenido", detalleFactura.iva_no_retenido],
                    ["Otro Impuesto", detalleFactura.valor_otro_impuesto],
                  ].filter(([, v]) => v && v > 0).map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-border py-1">
                      <span className="text-muted-foreground">{k}</span>
                      <span className="num font-medium">{fmtCLP(v)}</span>
                    </div>
                  ))}
                  <div className="col-span-2 flex justify-between bg-primary text-primary-foreground px-3 py-2 rounded-sm mt-2">
                    <span className="font-semibold">TOTAL</span>
                    <span className="num font-bold">{fmtCLP(detalleFactura.total)}</span>
                  </div>
                </div>
              </div>
              {detalleFactura.sii_data && Object.keys(detalleFactura.sii_data).length > 0 && (
                <div className="border-t border-border pt-3">
                  <div className="label-caps mb-2">Datos originales del SII (sin procesar)</div>
                  <div className="bg-background rounded-sm p-3 text-xs font-mono max-h-64 overflow-y-auto">
                    {Object.entries(detalleFactura.sii_data).map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-border py-1">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="text-foreground">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Asientos Dialog */}
      <Dialog open={previewOpen} onOpenChange={(v) => { if (!v && !previewSaving) { setPreviewOpen(false); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid={`${testidPrefix}-preview-dialog`}>
          <DialogHeader>
            <DialogTitle>Generar asientos contables desde facturas</DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Construyendo preview…</div>
          ) : previews.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No hay facturas pendientes de asiento en el período seleccionado.
            </div>
          ) : (
            <>
              <div className="bg-muted rounded-sm px-3 py-2 text-xs text-foreground mb-3 flex items-center justify-between flex-wrap gap-2">
                <span>
                  <strong>{previews.length}</strong> facturas con asiento propuesto.
                  Puedes editar los montos por línea y deseleccionar las que no quieras crear.
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-muted-foreground">Estado al crear:</span>
                  <Select value={previewEstado} onValueChange={setPreviewEstado}>
                    <SelectTrigger className="h-7 text-xs w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="borrador">Borrador (revisable)</SelectItem>
                      <SelectItem value="registrado">Registrado (firme)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3 mb-4" data-testid={`${testidPrefix}-preview-list`}>
                {previews.map((p, fi) => {
                  const cuadrado = Math.abs((p.total_debito || 0) - (p.total_credito || 0)) <= 1;
                  return (
                    <div key={p.factura_id}
                      className={`border rounded-md ${previewSelected[p.factura_id] ? "border-primary" : "border-border opacity-60"}`}
                      data-testid={`preview-row-${p.factura_id}`}>
                      <div className="flex items-center justify-between bg-background px-3 py-2 border-b border-border gap-2 flex-wrap">
                        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer flex-1 min-w-[200px]">
                          <input type="checkbox" checked={!!previewSelected[p.factura_id]}
                            data-testid={`preview-check-${p.factura_id}`}
                            onChange={(e) => setPreviewSelected({ ...previewSelected, [p.factura_id]: e.target.checked })} />
                          <span className="font-mono text-[12px]">#{p.factura_folio}</span>
                          <span>·</span>
                          <span>{fmtDate(p.factura_fecha)}</span>
                          <span>·</span>
                          <span className="font-mono text-[12px]">{p.contacto_rut}</span>
                          <span className="truncate">{p.contacto_nombre}</span>
                        </label>
                        <input
                          value={p.glosa_propuesta || ""}
                          onChange={(e) => updatePreviewGlosa(fi, e.target.value)}
                          data-testid={`preview-glosa-${p.factura_id}`}
                          className="flex-1 min-w-[180px] max-w-xs px-2 py-1 border border-border rounded-sm text-xs"
                          placeholder="Glosa del comprobante"
                        />
                        <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">{fmtCLP(p.total)}</span>
                      </div>
                      <table className="acc-table">
                        <thead>
                          <tr>
                            <th style={{ width: 200 }}>Cuenta</th>
                            <th>Glosa línea</th>
                            <th className="num" style={{ width: 130 }}>Debe</th>
                            <th className="num" style={{ width: 130 }}>Haber</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.lineas.map((l, li) => (
                            <tr key={li}>
                              <td>
                                <Select
                                  value={l.cuenta_id || ""}
                                  onValueChange={(v) => updatePreviewLinea(fi, li, "cuenta_id", v)}
                                >
                                  <SelectTrigger className="h-8 text-xs" data-testid={`preview-cuenta-${p.factura_id}-${li}`}>
                                    <SelectValue placeholder="Cuenta..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {cuentasImputables.map((c) => (
                                      <SelectItem key={c.id} value={c.id}>
                                        <span className="font-mono mr-1">{c.codigo}</span>{c.nombre}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td>
                                <input
                                  value={l.glosa || ""}
                                  onChange={(e) => updatePreviewLinea(fi, li, "glosa", e.target.value)}
                                  data-testid={`preview-glosa-linea-${p.factura_id}-${li}`}
                                  className="w-full px-2 py-1 border border-border rounded-sm text-xs"
                                />
                              </td>
                              <td className="num">
                                <input type="number" value={l.debito || 0}
                                  data-testid={`preview-debito-${p.factura_id}-${li}`}
                                  onChange={(e) => updatePreviewLinea(fi, li, "debito", e.target.value)}
                                  className="w-full px-2 py-1 border border-border rounded-sm text-sm text-right font-mono" />
                              </td>
                              <td className="num">
                                <input type="number" value={l.credito || 0}
                                  data-testid={`preview-credito-${p.factura_id}-${li}`}
                                  onChange={(e) => updatePreviewLinea(fi, li, "credito", e.target.value)}
                                  className="w-full px-2 py-1 border border-border rounded-sm text-sm text-right font-mono" />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className={`${cuadrado ? "bg-muted" : "bg-destructive/10"} font-semibold`}>
                            <td colSpan={2} className="text-right pr-3 label-caps">
                              {cuadrado ? "Cuadrado" : `Descuadrado (${fmtCLP((p.total_debito || 0) - (p.total_credito || 0), { showSign: true })})`}
                            </td>
                            <td className="num">{fmtCLP(p.total_debito)}</td>
                            <td className="num">{fmtCLP(p.total_credito)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  );
                })}
              </div>

              <DialogFooter>
                <div className="flex items-center justify-between w-full">
                  <span className="text-xs text-muted-foreground">
                    Seleccionadas: <strong>{Object.values(previewSelected).filter(Boolean).length}</strong> de {previews.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setPreviewOpen(false)}
                      className="border border-border hover:bg-muted text-sm px-4 py-2 rounded-sm">
                      Cancelar
                    </button>
                    <button type="button" onClick={confirmAsientos} disabled={previewSaving}
                      data-testid={`${testidPrefix}-confirm-asientos`}
                      className="bg-primary hover:bg-success disabled:opacity-50 text-primary-foreground text-sm font-medium px-4 py-2 rounded-sm">
                      {previewSaving ? "Creando…" : `Crear ${Object.values(previewSelected).filter(Boolean).length} asiento(s)`}
                    </button>
                  </div>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className={`text-sm mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function KPI({ label, value, tone = "default" }) {
  const tones = {
    default: "text-foreground",
    primary: "text-primary",
    warning: "text-warning-foreground",
  };
  return (
    <div className="bg-card border border-border rounded-md p-4">
      <div className="label-caps mb-1.5">{label}</div>
      <div className={`num text-lg font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export function LibroVentas() {
  return <LibroIVA tipo="venta" titulo="Libro de ventas"
    endpointLibro="/libro-ventas" endpointFacturas="/facturas-venta"
    endpointImport="/libro-ventas/import-sii" endpointContactos="/clientes"
    testidPrefix="lv" />;
}

export function LibroCompras() {
  return <LibroIVA tipo="compra" titulo="Libro de compras"
    endpointLibro="/libro-compras" endpointFacturas="/facturas-compra"
    endpointImport="/libro-compras/import-sii" endpointContactos="/proveedores"
    testidPrefix="lc" />;
}
